/**
 * VoiceAudioClips (#597) — admin-only inline panel listing user-audio
 * clips captured for one conversation, with playback. Lives on the
 * AdminConversationDetail page below CallDiagnostics.
 *
 * Empty when capture wasn't enabled for the user, or when the call
 * predates migration 0064. The panel hides itself in that case rather
 * than printing an empty title.
 *
 * Playback is auth-aware: <audio> tags can't carry Bearer headers, so
 * we fetch the stream via authenticated fetch + create a blob URL on
 * demand. URLs are revoked on unmount to keep memory bounded.
 *
 * #924 — the `Turn` column displays the combined-1-based chronological
 * label that matches the Turns grid + dealer-hand evidence chips
 * (Turn 1 = AI greeting, Turn 2 = elderly's first reply, ...). The
 * stored `voice_audio_clips.turn_idx` is user-turn-1-based (turn_idx=1
 * means "elderly's first reply"); display label = 2 * turn_idx. The
 * storage convention stays unchanged; conversion is presentational
 * only — symmetric with the conversion done in CardsHandView.js for
 * evidence chips and segment ranges.
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from './adminApi';

function fmtTime(iso) {
  if (!iso) return '';
  // #716 follow-up — UTC so it matches the server log timestamps (the
  // `[audio_end #381] reason=vad_speech ...` lines are in UTC). Pre-
  // fix this rendered in admin's local TZ which made cross-checking
  // a clip against the server-side trail require timezone math.
  const d = new Date(iso);
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
  return `${h}:${m}:${s}.${ms}Z`;
}

function fmtSize(b) {
  if (b === null || b === undefined) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// #924 — Convert the stored user-turn-1-based `turn_idx` to the
// combined-1-based chronological label used across the admin UI.
// User reply N is at transcript-jsonb idx 2N-1, which is label 2N.
// Exported for unit testing.
export function userTurnIdxToCombinedLabel(turn_idx) {
  if (turn_idx == null || !Number.isFinite(turn_idx) || turn_idx < 1) return null;
  return 2 * turn_idx;
}

async function fetchAudioObjectUrl(url) {
  const token = localStorage.getItem('am_admin_token');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Audio fetch failed (HTTP ${res.status})`);
  return URL.createObjectURL(await res.blob());
}

function ClipPlayer({ url }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let created = null;
    fetchAudioObjectUrl(url)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setBlobUrl(u);
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  if (error) return <div style={styles.error}>Audio: {error}</div>;
  if (!blobUrl) return <span style={styles.muted}>loading…</span>;
  return <audio controls src={blobUrl} style={{ maxWidth: 280 }} />;
}

export default function VoiceAudioClips({ convId }) {
  const [clips, setClips] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!convId) return;
    adminApi
      .listVoiceAudioClips(convId)
      .then((d) => setClips(d.clips || []))
      .catch((e) => setError(e.message));
  }, [convId]);

  // Hide the whole panel when there are no clips — avoids a misleading
  // "no clips" message for calls where capture was never enabled.
  if (!error && clips !== null && clips.length === 0) return null;

  return (
    <div style={styles.container}>
      <div style={styles.title}>
        Voice clips — opt-in mic recording (Internal — #597)
      </div>
      {error && <div style={styles.error}>Error: {error}</div>}
      {clips === null && !error && <div style={styles.muted}>Loading…</div>}
      {clips && clips.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th
                style={styles.th}
                title="Chronological 1-based turn label (#924). Matches the Turn column in the Call Diagnostics grid above and the evidence chips in the Dealer hand panel. User reply N is at label 2N (e.g. the elderly's first reply is Turn 2, since Turn 1 is the AI greeting). Storage stays user-turn-1-based; conversion is presentational."
              >
                Turn
              </th>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>SNR</th>
              <th style={styles.th}>Reason</th>
              <th style={styles.th}>Size</th>
              <th style={styles.th}>Transcript</th>
              <th style={styles.th}>Play</th>
            </tr>
          </thead>
          <tbody>
            {clips.map((c) => {
              // #924 — display combined-1-based label so this column
              // lines up with the Call Diagnostics Turns grid and
              // CardsHandView evidence chips. Tooltip shows the
              // underlying user-turn-1-based storage value for the
              // rare debugging case.
              const combinedLabel = userTurnIdxToCombinedLabel(c.turn_idx);
              return (
              <tr key={c.id} style={styles.tr}>
                <td
                  style={styles.td}
                  title={`storage turn_idx (user-turn-1-based): ${c.turn_idx}`}
                >
                  #{combinedLabel != null ? combinedLabel : c.turn_idx}
                </td>
                <td style={styles.td}>{fmtTime(c.created_at)}</td>
                <td style={styles.td}>
                  {c.snr !== null && c.snr !== undefined
                    ? c.snr.toFixed(2)
                    : '—'}
                </td>
                <td style={styles.td}>{c.audio_end_reason || '—'}</td>
                <td style={styles.td}>{fmtSize(c.size_bytes)}</td>
                <td style={{ ...styles.td, maxWidth: 280, whiteSpace: 'normal' }}>
                  {c.transcript || <span style={styles.muted}>(empty)</span>}
                </td>
                <td style={styles.td}>
                  <ClipPlayer url={c.stream_url} />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: '20px 24px',
    marginTop: 20,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#2C2C2C',
    marginBottom: 14,
    letterSpacing: '-0.01em',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    color: '#888',
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px dashed #f0ece6' },
  td: {
    padding: '8px',
    color: '#2C2C2C',
    fontFamily: 'Menlo, monospace',
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
  },
  error: { color: '#C0392B', fontSize: '0.85rem' },
  muted: { color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' },
};
